import { useState } from "react"
import { type VariantProps, cva } from "class-variance-authority"
import { cn } from "../../lib/utils"

const avatarVariants = cva(
  "inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-medium",
  {
    variants: {
      size: {
        sm: "h-[22px] w-[22px] text-[10px]",
        md: "h-8 w-8 text-xs",
        lg: "h-10 w-10 text-sm",
        xl: "h-16 w-16 text-lg",
      },
      tone: {
        muted: "bg-muted text-foreground",
        primary: "bg-primary/10 text-primary",
      },
    },
    defaultVariants: { size: "sm", tone: "muted" },
  }
)

export function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()
}

export interface AvatarProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof avatarVariants> {
  name: string
  /** Precomputed by the server when available; derived from name otherwise. */
  initials?: string
  /** Same-origin proxy URL from User#avatar_url; null/undefined shows initials. */
  avatarUrl?: string | null
}

// One person as a circle: their photo when we have one, initials otherwise.
// A photo that fails to load (deleted blob, offline) drops back to initials
// so the circle never renders empty.
function Avatar({ name, initials, avatarUrl, size, tone, className, ...rest }: AvatarProps) {
  const [broken, setBroken] = useState(false)
  const showImage = Boolean(avatarUrl) && !broken

  return (
    <span className={cn(avatarVariants({ size, tone }), className)} {...rest}>
      {showImage ? (
        <img
          src={avatarUrl ?? undefined}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        initials ?? initialsFor(name)
      )}
    </span>
  )
}

export { Avatar, avatarVariants }
